import {
  AnyAggregate,
  ColumnValueOption,
  DatasetPayload,
  Format,
  FormatAmenityArray,
  FormatDate,
  FormatMoney,
  FormatNumber,
  FormatPercentage,
  MarketColumns,
  PercentileAggregate,
  PriceColumns,
  PropertyColumns,
  ReportColumns,
  RentRollColumns,
  ScopeColumn,
  StaticAggregate,
  UnitColumns,
  LeasingActivityColumns,
  RelativeDate
} from '../clients'
import { allBuildingAmenities, allMsas, allStates, allUnitAmenities } from '../datasetValueOptions'
import {
  format,
  subDays,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  subMonths,
  subWeeks
} from 'date-fns'

export type AnyColumn =
  | PropertyColumns
  | MarketColumns
  | UnitColumns
  | PriceColumns
  | RentRollColumns
  | ReportColumns
  | LeasingActivityColumns

const rentRollColumnSet = new Set<string>(Object.values(RentRollColumns))
const rentRollAggregationColumnSet = new Set<string>([
  RentRollColumns.RENT_ROLL_AGGREGATION_BED_BATH,
  RentRollColumns.RENT_ROLL_AGGREGATION_BED_BATH_SQFT,
  RentRollColumns.RENT_ROLL_AGGREGATION_FLOORPLAN
])
const rentRollMetricColumnSet = new Set<string>([
  RentRollColumns.RENT_ROLL_ASKING_RENT,
  RentRollColumns.RENT_ROLL_IN_PLACE_RENT,
  RentRollColumns.RENT_ROLL_ESTIMATED_UNITS,
  RentRollColumns.RENT_ROLL_NUMBER_LISTINGS,
  RentRollColumns.RENT_ROLL_EXPOSURE_PERCENT,
  RentRollColumns.RENT_ROLL_RENEWAL_PERCENT,
  RentRollColumns.RENT_ROLL_DAYS_ON_MARKET,
  RentRollColumns.RENT_ROLL_SQFT,
  RentRollColumns.RENT_ROLL_LEASE_TRADEOUT,
  RentRollColumns.RENT_ROLL_TREND
])
const unitPriceOccupancyColumnSet = new Set<string>([
  ...Object.values(UnitColumns),
  ...Object.values(PriceColumns),
  ...Object.values(LeasingActivityColumns)
])

export function isRentRollColumn(column: AnyColumn | string): column is RentRollColumns {
  return rentRollColumnSet.has(column)
}

export function isUnitPriceOccupancyColumn(column: AnyColumn | string): boolean {
  return unitPriceOccupancyColumnSet.has(column)
}

export function isRentRollAggregationColumn(column: AnyColumn | string): boolean {
  return rentRollAggregationColumnSet.has(column)
}

export function isRentRollMetricColumn(column: AnyColumn | string): boolean {
  return rentRollMetricColumnSet.has(column)
}

export function hasRentRollMetricWithoutAggregation(
  columns: readonly (AnyColumn | string)[]
): boolean {
  let hasMetric = false
  let hasAggregation = false

  for (const column of columns) {
    if (isRentRollMetricColumn(column)) {
      hasMetric = true
    } else if (isRentRollAggregationColumn(column)) {
      hasAggregation = true
    }
  }

  return hasMetric && !hasAggregation
}

export function hasMultipleRentRollAggregations(columns: readonly (AnyColumn | string)[]): boolean {
  let aggregationCount = 0

  for (const column of columns) {
    if (isRentRollAggregationColumn(column)) {
      aggregationCount += 1
      if (aggregationCount > 1) {
        return true
      }
    }
  }

  return false
}

export function hasRentRollCompatibilityConflict(
  columns: readonly (AnyColumn | string)[]
): boolean {
  let hasRentRoll = false
  let hasUnitPriceOccupancy = false

  for (const column of columns) {
    if (isRentRollColumn(column)) {
      hasRentRoll = true
    } else if (isUnitPriceOccupancyColumn(column)) {
      hasUnitPriceOccupancy = true
    }

    if (hasRentRoll && hasUnitPriceOccupancy) {
      return true
    }
  }

  return false
}

export function isRelativeDate(value: any): value is RelativeDate {
  return (
    typeof value === 'object' &&
    value !== null &&
    'relativeDate' in value &&
    typeof value.relativeDate === 'string' &&
    [
      'lastMonth',
      'lastQuarter',
      'lastNDays',
      'thisMonth',
      'thisQuarter',
      'lastWeek',
      'yesterday',
      'today'
    ].includes(value.relativeDate)
  )
}

export function resolveRelativeDate(
  relativeDate: RelativeDate,
  referenceDate: Date = new Date()
): string {
  const now = referenceDate
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  switch (relativeDate.relativeDate) {
    case 'lastMonth': {
      const lastMonth = subMonths(today, 1)
      const start = startOfMonth(lastMonth)
      const end = endOfMonth(lastMonth)
      return format(end, 'yyyy-MM-dd')
    }
    case 'lastQuarter': {
      const lastQuarter = subMonths(today, 3)
      const start = startOfQuarter(lastQuarter)
      const end = endOfQuarter(lastQuarter)
      return format(end, 'yyyy-MM-dd')
    }
    case 'lastNDays': {
      if (!relativeDate.days || relativeDate.days <= 0) {
        throw new Error('days parameter is required and must be positive for lastNDays')
      }
      const nDaysAgo = subDays(today, relativeDate.days)
      return format(nDaysAgo, 'yyyy-MM-dd')
    }
    case 'thisMonth': {
      return format(today, 'yyyy-MM-dd')
    }
    case 'thisQuarter': {
      return format(today, 'yyyy-MM-dd')
    }
    case 'lastWeek': {
      const weekAgo = subWeeks(today, 1)
      return format(weekAgo, 'yyyy-MM-dd')
    }
    case 'yesterday': {
      const yesterday = subDays(today, 1)
      return format(yesterday, 'yyyy-MM-dd')
    }
    case 'today': {
      return format(today, 'yyyy-MM-dd')
    }
    default:
      throw new Error(`Unknown relative date type: ${(relativeDate as any).relativeDate}`)
  }
}

// Helper function to resolve relative date for "from" values (start of period)
export function resolveRelativeDateFrom(
  relativeDate: RelativeDate,
  referenceDate: Date = new Date()
): string {
  const now = referenceDate
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  switch (relativeDate.relativeDate) {
    case 'lastMonth': {
      const lastMonth = subMonths(today, 1)
      const start = startOfMonth(lastMonth)
      return format(start, 'yyyy-MM-dd')
    }
    case 'lastQuarter': {
      const lastQuarter = subMonths(today, 3)
      const start = startOfQuarter(lastQuarter)
      return format(start, 'yyyy-MM-dd')
    }
    case 'lastNDays': {
      if (!relativeDate.days || relativeDate.days <= 0) {
        throw new Error('days parameter is required and must be positive for lastNDays')
      }
      const nDaysAgo = subDays(today, relativeDate.days)
      return format(nDaysAgo, 'yyyy-MM-dd')
    }
    case 'thisMonth': {
      const start = startOfMonth(today)
      return format(start, 'yyyy-MM-dd')
    }
    case 'thisQuarter': {
      const start = startOfQuarter(today)
      return format(start, 'yyyy-MM-dd')
    }
    case 'lastWeek': {
      const weekAgo = subWeeks(today, 1)
      return format(weekAgo, 'yyyy-MM-dd')
    }
    case 'yesterday': {
      const yesterday = subDays(today, 1)
      return format(yesterday, 'yyyy-MM-dd')
    }
    case 'today': {
      return format(today, 'yyyy-MM-dd')
    }
    default:
      throw new Error(`Unknown relative date type: ${(relativeDate as any).relativeDate}`)
  }
}
export type ColumnType =
  | 'uuid'
  | 'string'
  | 'integer'
  | 'float'
  | 'boolean'
  | 'array_string'
  | 'date'
  | 'money'
  | 'percentage'
  | 'array_string_with_flag'
  | 'geography'

export interface ColumnInfo {
  description: string // the description of the column, for the data dictionary and help text
  category: string // the category of the column. Used to group columns in the UI
  type: ColumnType // the type of the column
  nullable: boolean // if the column can be null
  aggregations: AnyAggregate[] // list of available aggregations for the column
  label?: string // set the default label for the column
  format?: Format // set the default format for the column
  valueSettings?: {
    // this is for columns who instead of letting the user type a value, the value is picked from a list
    allValueOptions: ColumnValueOption[]
    filteredOptions: ColumnValueOption[]
  }
  flagLabel?: string
  usage: {
    column: boolean // if true, the column will be able to be picked as a column
    mustAggregate: boolean // if true, the column will have to be aggregated by the user
    filter: boolean // if true, users will be able to pick it as a filter
  }
  exclusivityKey?: string // two columns with the same exclusivity key will not be able to be used together in a query
  isRecommended?: boolean // if true, the column will be highlighted as recommended in the UI
  cast?: 'FLOAT' | 'USMALLINT' // column casts for a specific aggregation type
}

const generatePercentileAggregations = (percentiles: number[]): PercentileAggregate[] => {
  return percentiles.map((percentile) => ({
    percentile
  }))
}

export const columnData: Record<AnyColumn, ColumnInfo> = {
  // Report Columns
  [ReportColumns.REPORT_TAG]: {
    description: 'Choose a tag to select all of the subjects (and/or comps) from matching reports.',
    type: 'array_string_with_flag',
    category: 'Report Characteristics',
    nullable: false,
    aggregations: [],
    usage: {
      column: false,
      mustAggregate: false,
      filter: true
    },
    flagLabel: 'Include Comps'
  },
  [ReportColumns.REPORT_ID]: {
    description: 'The Report ID that the property belongs to.',
    type: 'uuid',
    category: 'Report Characteristics',
    nullable: false,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: false
    }
  },
  [ReportColumns.REPORT_NAME]: {
    description: 'The name of the Report that the property belongs to.',
    type: 'string',
    category: 'Report Characteristics',
    nullable: false,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: false
    }
  },
  [ReportColumns.IS_SUBJECT]: {
    description: 'Flag indicating if the property is the primary subject of a report.',
    type: 'boolean',
    category: 'Report Characteristics',
    nullable: false,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: false
    }
  },
  // Market Columns
  [MarketColumns.MSA]: {
    description: 'Metropolitan Statistical Area where the building is located',
    category: 'Market',
    label: 'MSA',
    type: 'string',
    nullable: true,
    aggregations: [StaticAggregate.COUNT, StaticAggregate.COUNT_DISTINCT],
    valueSettings: {
      allValueOptions: allMsas,
      filteredOptions: allMsas
    },
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [MarketColumns.STATE]: {
    description: 'State where the building is located',
    category: 'Market',
    type: 'string',
    nullable: false,
    aggregations: [StaticAggregate.COUNT, StaticAggregate.COUNT_DISTINCT],
    valueSettings: {
      allValueOptions: allStates,
      filteredOptions: allStates
    },
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    isRecommended: true
  },
  [MarketColumns.CITY]: {
    description: 'City where the building is located',
    category: 'Market',
    type: 'string',
    nullable: false,
    aggregations: [StaticAggregate.COUNT, StaticAggregate.COUNT_DISTINCT],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [MarketColumns.ZIP_CODE]: {
    description: 'ZIP code of the building. 5 digits',
    category: 'Market',
    type: 'string',
    nullable: false,
    aggregations: [StaticAggregate.COUNT, StaticAggregate.COUNT_DISTINCT],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [MarketColumns.GEOGRAPHY]: {
    description: 'Draw borders to restrict data to a certain region.',
    category: 'Market',
    type: 'geography',
    nullable: false,
    aggregations: [],
    usage: {
      column: false,
      mustAggregate: false,
      filter: true
    },
    isRecommended: true
  },

  // Property Columns
  [PropertyColumns.STREET_ADDRESS]: {
    description: 'Street address of the building',
    type: 'string',
    category: 'Property Characteristics',
    nullable: false,
    aggregations: [StaticAggregate.COUNT, StaticAggregate.COUNT_DISTINCT],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.LAT]: {
    description: 'Latitude of the building',
    type: 'float',
    category: 'Property Characteristics',
    nullable: false,
    aggregations: [StaticAggregate.AVG, StaticAggregate.MIN, StaticAggregate.MAX],
    format: {
      format: FormatNumber.format.NUMBER,
      decimals: 5,
      thousandsSeparator: true
    } as FormatNumber,
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    cast: 'FLOAT'
  },
  [PropertyColumns.LON]: {
    description: 'Longitude of the building',
    type: 'float',
    category: 'Property Characteristics',
    nullable: false,
    aggregations: [StaticAggregate.AVG, StaticAggregate.MIN, StaticAggregate.MAX],
    format: {
      format: FormatNumber.format.NUMBER,
      decimals: 5,
      thousandsSeparator: true
    } as FormatNumber,
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    cast: 'FLOAT'
  },
  [PropertyColumns.BUILDING_NAME]: {
    description: 'Name of the building or community',
    type: 'string',
    category: 'Property Characteristics',
    nullable: true,
    aggregations: [StaticAggregate.COUNT, StaticAggregate.COUNT_DISTINCT],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.NUMBER_UNITS]: {
    description: 'Number of units in the building',
    type: 'integer',
    category: 'Property Characteristics',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    isRecommended: true,
    cast: 'FLOAT'
  },
  [PropertyColumns.YEAR_BUILT]: {
    description: 'Year the building was constructed',
    type: 'integer',
    category: 'Property Characteristics',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    format: {
      format: FormatNumber.format.NUMBER,
      decimals: 0,
      thousandsSeparator: false
    } as FormatNumber,
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    cast: 'FLOAT'
  },
  [PropertyColumns.NUMBER_STORIES]: {
    description: 'Number of stories in the building',
    type: 'integer',
    category: 'Property Characteristics',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    cast: 'FLOAT'
  },
  [PropertyColumns.NUMBER_STORIES_PREDICTION]: {
    description:
      'Number of stories in the building. Computed using our proprietary Computer Vision models looking at images of the facade',
    type: 'string',
    category: 'Property Characteristics',
    nullable: true,
    aggregations: [StaticAggregate.COUNT, StaticAggregate.COUNT_DISTINCT],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.MANAGEMENT_COMPANY]: {
    description: 'Company managing the building',
    type: 'string',
    category: 'Property Characteristics',
    nullable: true,
    aggregations: [StaticAggregate.COUNT, StaticAggregate.COUNT_DISTINCT],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.BUILDING_WEBSITE]: {
    description: 'Property website URL',
    type: 'string',
    category: 'Property Characteristics',
    nullable: true,
    aggregations: [StaticAggregate.COUNT, StaticAggregate.COUNT_DISTINCT],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.IS_APARTMENT]: {
    description:
      'Boolean indicating if the property is an apartment. Based on publicly reported information.',
    type: 'boolean',
    category: 'Propery Type',
    nullable: true,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.IS_CONDO]: {
    description:
      'Boolean indicating if the property is a condo. Based on publicly reported information.',
    type: 'boolean',
    category: 'Propery Type',
    nullable: true,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.IS_SINGLE_FAMILY]: {
    description:
      'Boolean indicating if the property is a single family home. Based on publicly reported information.',
    type: 'boolean',
    category: 'Propery Type',
    nullable: true,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.IS_STUDENT]: {
    description: 'Detected student housing based on text and price analysis on the property.',
    type: 'boolean',
    category: 'Propery Type',
    nullable: true,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.IS_SENIOR]: {
    description: 'Detected senior housing based on text analysis on the property.',
    type: 'boolean',
    category: 'Propery Type',
    nullable: true,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.IS_AFFORDABLE]: {
    description: 'Detected affordable housing based on the LIHTC database, or floorplans names.',
    type: 'boolean',
    category: 'Propery Type',
    nullable: true,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.IS_BUILD_TO_RENT]: {
    description: 'Detected build-to-rent housing based on text and data analysis on the property.',
    type: 'boolean',
    category: 'Propery Type',
    nullable: true,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  // Fees
  [PropertyColumns.CATS_MONTHLY_RENT]: {
    description: 'Monthly rent amount for cats',
    type: 'money',
    category: 'Fees',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.CATS_ONE_TIME_FEE]: {
    description: 'One-time fee for cats',
    type: 'money',
    category: 'Fees',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.CATS_DEPOSIT]: {
    description: 'Deposit amount for cats',
    type: 'money',
    category: 'Fees',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.DOGS_MONTHLY_RENT]: {
    description: 'Monthly rent amount for dogs',
    type: 'money',
    category: 'Fees',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.DOGS_ONE_TIME_FEE]: {
    description: 'One-time fee for dogs',
    type: 'money',
    category: 'Fees',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.DOGS_DEPOSIT]: {
    description: 'Deposit amount for dogs',
    type: 'money',
    category: 'Fees',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.ADMIN_FEE]: {
    description: 'Administrative fee',
    type: 'money',
    category: 'Fees',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.AMENITY_FEE]: {
    description: 'Amenity fee',
    type: 'money',
    category: 'Fees',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.APPLICATION_FEE]: {
    description: 'Application fee',
    type: 'money',
    category: 'Fees',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.STORAGE_FEE]: {
    description: 'Storage fee',
    type: 'money',
    category: 'Fees',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.PARKING_COVERED]: {
    description: 'Covered parking fee',
    type: 'money',
    category: 'Fees',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.PARKING_GARAGE]: {
    description: 'Garage parking fee',
    type: 'money',
    category: 'Fees',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.PARKING_SURFACE_LOT]: {
    description: 'Surface lot parking fee',
    type: 'money',
    category: 'Fees',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.BUILDING_AMENITIES]: {
    description: 'Array of building amenities',
    type: 'array_string',
    category: 'Amenities',
    nullable: false,
    format: {
      format: FormatAmenityArray.format.AMENITY_ARRAY
    } as FormatAmenityArray,
    aggregations: [],
    usage: {
      column: false,
      mustAggregate: false,
      filter: true
    },
    valueSettings: {
      allValueOptions: allBuildingAmenities,
      filteredOptions: allBuildingAmenities
    }
  },
  [PropertyColumns.UNIT_AMENITIES]: {
    description: 'Array of unit amenities',
    type: 'array_string',
    category: 'Amenities',
    nullable: false,
    format: {
      format: FormatAmenityArray.format.AMENITY_ARRAY
    } as FormatAmenityArray,
    aggregations: [],
    usage: {
      column: false,
      mustAggregate: false,
      filter: true
    },
    valueSettings: {
      allValueOptions: allUnitAmenities,
      filteredOptions: allUnitAmenities
    }
  },
  [PropertyColumns.PROPERTY_ID]: {
    description:
      'Unique identifier for a building. We try to keep this stable but it can change if the property is significantly updated, or ends up being merged with another property',
    type: 'uuid',
    category: 'Identifier',
    nullable: false,
    aggregations: [StaticAggregate.COUNT, StaticAggregate.COUNT_DISTINCT],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  // Quality Score Columns
  [PropertyColumns.OVERALL_QUALITY_SCORE]: {
    description:
      'Overall quality prediction of the property. Weighted average of individual room predictions',
    type: 'percentage',
    category: 'Quality Score',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    cast: 'FLOAT'
  },
  [PropertyColumns.QUALITY_SCORE_BEDROOM]: {
    description:
      'Average quality score for the bedroom. Computed using Computer Vision models looking at the images of the property',
    type: 'percentage',
    category: 'Quality Score',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.QUALITY_SCORE_KITCHEN]: {
    description:
      'Average quality score for the kitchen. Computed using Computer Vision models looking at the images of the property',
    type: 'percentage',
    category: 'Quality Score',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.QUALITY_SCORE_BATHROOM]: {
    description:
      'Average quality score for the bathroom. Computed using Computer Vision models looking at the images of the property',
    type: 'percentage',
    category: 'Quality Score',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.QUALITY_SCORE_DINING_ROOM]: {
    description:
      'Average quality score for the dining room. Computed using Computer Vision models looking at the images of the property',
    type: 'percentage',
    category: 'Quality Score',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.QUALITY_SCORE_COMMON_AREAS]: {
    description:
      'Average quality score for common areas. Computed using Computer Vision models looking at the images of the property',
    type: 'percentage',
    category: 'Quality Score',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.QUALITY_SCORE_FITNESS_CENTER]: {
    description:
      'Average quality score for the fitness center. Computed using Computer Vision models looking at the images of the property',
    type: 'percentage',
    category: 'Quality Score',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.QUALITY_SCORE_LAUNDRY_ROOM]: {
    description:
      'Average quality score for the laundry room. Computed using Computer Vision models looking at the images of the property',
    type: 'percentage',
    category: 'Quality Score',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.QUALITY_SCORE_LIVING_ROOM]: {
    description:
      'Average quality score for the living room. Computed using Computer Vision models looking at the images of the property',
    type: 'percentage',
    category: 'Quality Score',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.QUALITY_SCORE_MAIN_ENTRANCE]: {
    description:
      'Average quality score for the main entrance. Computed using Computer Vision models looking at the images of the property',
    type: 'percentage',
    category: 'Quality Score',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.QUALITY_SCORE_STAIRS_HALLWAY]: {
    description:
      'Average quality score for the stairs hallway. Computed using Computer Vision models looking at the images of the property',
    type: 'percentage',
    category: 'Quality Score',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.QUALITY_SCORE_SWIMMING_POOL]: {
    description:
      'Average quality score for the swimming pool. Computed using Computer Vision models looking at the images of the property',
    type: 'percentage',
    category: 'Quality Score',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.QUALITY_SCORE_WALK_IN_CLOSET]: {
    description:
      'Average quality score for the walk-in closet. Computed using Computer Vision models looking at the images of the property',
    type: 'percentage',
    category: 'Quality Score',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },

  // Pricing Strategy Columns
  [PropertyColumns.AVG_DURATION_DAYS]: {
    description:
      'Average number of days a price is active before changing. Only for properties with at least 3 different prices',
    type: 'float',
    label: 'Avg Price Duration Days',
    category: 'Pricing Strategy',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    cast: 'FLOAT'
  },
  [PropertyColumns.AVG_PRICE_CHANGE]: {
    description:
      'Average price change between the first and last price for the unit in the property. Only for properties with at least 3 different prices',
    type: 'float',
    category: 'Pricing Strategy',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    cast: 'FLOAT'
  },
  [PropertyColumns.AVG_TIME_ON_MARKET_DAYS]: {
    description:
      'Average number of days a unit is on the market for the property. Only for properties with at least 3 different prices',
    category: 'Pricing Strategy',
    type: 'float',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },

  // Pricing Strategy Columns
  [PropertyColumns.COUNT_PRICES]: {
    description:
      'Number of prices seen for the property. Only for properties with at least 3 different prices',
    type: 'integer',
    category: 'Pricing Strategy',
    nullable: true,
    aggregations: [StaticAggregate.COUNT, StaticAggregate.COUNT_DISTINCT],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.IS_USING_REV_MANAGEMENT]: {
    description:
      'Boolean indicating if the property is likely using revenue management. Based on how frequently prices change. Only for properties with at least 3 different prices',
    type: 'boolean',
    category: 'Pricing Strategy',
    nullable: true,
    aggregations: [StaticAggregate.SUM, StaticAggregate.AVG],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [LeasingActivityColumns.OCCUPANCY_OVER_TIME_AT_DATE]: {
    label: 'As of Day',
    description: 'The date that the leased percentage and exposure percentage was captured.',
    type: 'date',
    category: 'Leasing Activity',
    nullable: false,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    exclusivityKey: 'time_breakdown'
  },
  [LeasingActivityColumns.LEASED_PERCENTAGE]: {
    description:
      'The ratio of vacant units to total units. For stabilized assets, vacant units are defined as those available for move-in within the next 7 days.',
    type: 'percentage',
    category: 'Leasing Activity',
    nullable: false,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    },
    format: {
      format: FormatPercentage.format.PERCENTAGE,
      decimals: 1
    } as FormatPercentage,
    cast: 'FLOAT'
  },
  [LeasingActivityColumns.EXPOSURE_PERCENTAGE]: {
    description:
      'Percentage of units in a building currently listed as available for move-in within the next 6 months. This includes units that are either already vacant or not being renewed, divided by the total number of units in the building.',
    type: 'percentage',
    category: 'Leasing Activity',
    nullable: false,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    },
    format: {
      format: FormatPercentage.format.PERCENTAGE,
      decimals: 1
    } as FormatPercentage,
    cast: 'FLOAT'
  },
  [PropertyColumns.IS_LEASE_UP]: {
    description: 'A boolean indicating whether a property is currently in lease-up',
    type: 'boolean',
    category: 'Propery Type',
    nullable: true,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },

  // Review Columns
  [PropertyColumns.COUNT_REVIEWS]: {
    description:
      'Number of reviews for the property. Only when the property has some public reviews.',
    type: 'integer',
    category: 'Tenant Reviews',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.AVG_REVIEW_SCORE]: {
    description: 'Average review score for the property',
    type: 'percentage',
    category: 'Tenant Reviews',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.POSITIVE_STAFF_AND_MANAGEMENT]: {
    description: 'Positive review score count for the staff and management',
    type: 'integer',
    category: 'Tenant Reviews',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.POSITIVE_AMENITIES_AND_FEATURES]: {
    description: 'Positive review score count for the amenities and features',
    type: 'integer',
    category: 'Tenant Reviews',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.POSITIVE_CLEANLINESS_AND_MAINTENANCE]: {
    description: 'Positive review score count for the cleanliness and maintenance',
    type: 'integer',
    category: 'Tenant Reviews',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.POSITIVE_LOCATION]: {
    description: 'Positive review score count for the location',
    type: 'integer',
    category: 'Tenant Reviews',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.POSITIVE_VALUE]: {
    description: 'Positive review score count for the value',
    type: 'integer',
    category: 'Tenant Reviews',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },

  [PropertyColumns.NEGATIVE_STAFF_AND_MANAGEMENT]: {
    description: 'Negative review score count for the staff and management',
    type: 'integer',
    category: 'Tenant Reviews',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.NEGATIVE_AMENITIES_AND_FEATURES]: {
    description: 'Negative review score count for the amenities and features',
    type: 'integer',
    category: 'Tenant Reviews',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.NEGATIVE_CLEANLINESS_AND_MAINTENANCE]: {
    description: 'Negative review score count for the cleanliness and maintenance',
    type: 'integer',
    category: 'Tenant Reviews',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.NEGATIVE_LOCATION]: {
    description: 'Negative review score count for the location',
    type: 'integer',
    category: 'Tenant Reviews',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [PropertyColumns.NEGATIVE_VALUE]: {
    description: 'Negative review score count for the value',
    type: 'integer',
    category: 'Tenant Reviews',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },

  // Unit Type Columns
  [UnitColumns.BED]: {
    description: 'Number of bedrooms in the unit',
    type: 'integer',
    category: 'Unit Type',
    nullable: false,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [UnitColumns.BATH]: {
    description: 'Number of full bathrooms in the unit',
    type: 'integer',
    category: 'Unit Type',
    nullable: false,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [UnitColumns.PARTIAL_BATH]: {
    description: 'Number of partial bathrooms in the unit',
    type: 'integer',
    category: 'Unit Type',
    nullable: false,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  // Unit Columns
  [UnitColumns.FLOORPLAN_NAME]: {
    description: 'Name of the floorplan',
    type: 'string',
    category: 'Unit Characteristics',
    nullable: true,
    aggregations: [StaticAggregate.COUNT, StaticAggregate.COUNT_DISTINCT],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    }
  },
  [UnitColumns.FLOOR]: {
    description:
      'Estimated floor number of the unit. This is estimated from patterns AI could detected in unit names for that building',
    type: 'integer',
    category: 'Unit Characteristics',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    }
  },
  [UnitColumns.SQFT]: {
    description: 'Advertised square footage of the unit',
    type: 'integer',
    category: 'Unit Characteristics',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    },
    cast: 'USMALLINT'
  },
  [UnitColumns.DAYS_ON_MARKET]: {
    description: 'Number of days the unit has been on the market',
    type: 'integer',
    category: 'Unit Characteristics',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    },
    cast: 'USMALLINT'
  },

  [UnitColumns.UNIT_ID]: {
    description: 'Unique identifier for the unit. This can change',
    type: 'integer',
    category: 'Identifier',
    nullable: false,
    aggregations: [StaticAggregate.COUNT_DISTINCT],
    usage: {
      column: true,
      mustAggregate: true,
      filter: false
    },
    label: 'Distinct Units'
  },

  // Rent Roll Columns
  [RentRollColumns.RENT_ROLL_AGGREGATION_BED_BATH]: {
    description:
      'Select this to aggregate rent roll rows by bed and bath (group_by = unit_type). One aggregation selector is required before selecting Rent Roll metrics.',
    type: 'string',
    category: 'Rent Roll Aggregation',
    nullable: false,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: false
    },
    label: 'Aggregation: Bed / Bath',
    exclusivityKey: 'rent_roll_aggregation_level',
    isRecommended: true
  },
  [RentRollColumns.RENT_ROLL_AGGREGATION_BED_BATH_SQFT]: {
    description:
      'Select this to aggregate rent roll rows by bed, bath, and sqft (group_by = bed_bath_sf). One aggregation selector is required before selecting Rent Roll metrics.',
    type: 'string',
    category: 'Rent Roll Aggregation',
    nullable: false,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: false
    },
    label: 'Aggregation: Bed / Bath / Sqft',
    exclusivityKey: 'rent_roll_aggregation_level',
    isRecommended: true
  },
  [RentRollColumns.RENT_ROLL_AGGREGATION_FLOORPLAN]: {
    description:
      'Select this to aggregate rent roll rows by floorplan (group_by = floorplan). One aggregation selector is required before selecting Rent Roll metrics.',
    type: 'string',
    category: 'Rent Roll Aggregation',
    nullable: false,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: false
    },
    label: 'Aggregation: Floorplan',
    exclusivityKey: 'rent_roll_aggregation_level',
    isRecommended: true
  },
  [RentRollColumns.RENT_ROLL_FLOORPLAN_NAME]: {
    description: 'Floorplan name from the rent roll snapshot.',
    type: 'string',
    category: 'Rent Roll',
    nullable: true,
    aggregations: [StaticAggregate.COUNT, StaticAggregate.COUNT_DISTINCT],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    label: 'Rent Roll Floorplan Name'
  },
  [RentRollColumns.RENT_ROLL_BED]: {
    description: 'Bedroom count from the rent roll snapshot.',
    type: 'integer',
    category: 'Rent Roll',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    label: 'Rent Roll Bed'
  },
  [RentRollColumns.RENT_ROLL_BATH]: {
    description: 'Bathroom count from the rent roll snapshot.',
    type: 'integer',
    category: 'Rent Roll',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    label: 'Rent Roll Bath'
  },
  [RentRollColumns.RENT_ROLL_PARTIAL_BATH]: {
    description: 'Partial bathroom count from the rent roll snapshot.',
    type: 'integer',
    category: 'Rent Roll',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    label: 'Rent Roll Partial Bath'
  },
  [RentRollColumns.RENT_ROLL_ASKING_RENT]: {
    description: 'Asking rent from the rent roll snapshot.',
    type: 'money',
    category: 'Rent Roll',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    label: 'Rent Roll Asking Rent',
    cast: 'FLOAT'
  },
  [RentRollColumns.RENT_ROLL_IN_PLACE_RENT]: {
    description: 'In-place rent from the rent roll snapshot.',
    type: 'money',
    category: 'Rent Roll',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    label: 'Rent Roll In-Place Rent',
    cast: 'FLOAT'
  },
  [RentRollColumns.RENT_ROLL_ESTIMATED_UNITS]: {
    description: 'Estimated unit count from the rent roll snapshot.',
    type: 'integer',
    category: 'Rent Roll',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    label: 'Rent Roll Estimated Units',
    cast: 'FLOAT'
  },
  [RentRollColumns.RENT_ROLL_NUMBER_LISTINGS]: {
    description: 'Number of listings in the rent roll snapshot for the aggregation level.',
    type: 'integer',
    category: 'Rent Roll',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    label: 'Rent Roll Number Listings',
    cast: 'USMALLINT'
  },
  [RentRollColumns.RENT_ROLL_EXPOSURE_PERCENT]: {
    description:
      'Exposure percentage from the rent roll snapshot (share of units currently exposed/listed relative to relevant base).',
    type: 'percentage',
    category: 'Rent Roll',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    label: 'Rent Roll Exposure %',
    format: {
      format: FormatPercentage.format.PERCENTAGE,
      decimals: 1
    } as FormatPercentage,
    cast: 'FLOAT'
  },
  [RentRollColumns.RENT_ROLL_RENEWAL_PERCENT]: {
    description: 'Renewal percentage from the rent roll snapshot.',
    type: 'percentage',
    category: 'Rent Roll',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    label: 'Rent Roll Renewal %',
    format: {
      format: FormatPercentage.format.PERCENTAGE,
      decimals: 1
    } as FormatPercentage,
    cast: 'FLOAT'
  },
  [RentRollColumns.RENT_ROLL_DAYS_ON_MARKET]: {
    description: 'Average days on market from the rent roll snapshot.',
    type: 'integer',
    category: 'Rent Roll',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    label: 'Rent Roll Days on Market',
    cast: 'USMALLINT'
  },
  [RentRollColumns.RENT_ROLL_SQFT]: {
    description: 'Average square footage from the rent roll snapshot for the aggregation level.',
    type: 'integer',
    category: 'Rent Roll',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    label: 'Rent Roll Sqft',
    cast: 'USMALLINT'
  },
  [RentRollColumns.RENT_ROLL_LEASE_TRADEOUT]: {
    description:
      'Lease trade-out from the rent roll snapshot (e.g. lease trade-out percent between new and prior rents).',
    type: 'percentage',
    category: 'Rent Roll',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    label: 'Rent Roll Lease Trade-Out',
    format: {
      format: FormatPercentage.format.PERCENTAGE,
      decimals: 1
    } as FormatPercentage,
    cast: 'FLOAT'
  },
  [RentRollColumns.RENT_ROLL_TREND]: {
    description: 'Rent roll trend metric (e.g. recent rent trend or exposure trend).',
    type: 'percentage',
    category: 'Rent Roll',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: false,
      filter: true
    },
    label: 'Rent Roll Trend',
    format: {
      format: FormatPercentage.format.PERCENTAGE,
      decimals: 1
    } as FormatPercentage,
    cast: 'FLOAT'
  },
  // Price Columns
  [PriceColumns.FROM_DATE]: {
    description: 'Date when the price was effective',
    type: 'date',
    category: 'Price History',
    nullable: true,
    aggregations: [StaticAggregate.MIN, StaticAggregate.MAX],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    }
  },
  [PriceColumns.TO_DATE]: {
    description: 'Date when the price was effective',
    type: 'date',
    category: 'Price History',
    nullable: true,
    aggregations: [StaticAggregate.MIN, StaticAggregate.MAX],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    }
  },
  [PriceColumns.ASKING_RENT]: {
    description: 'Asking rent for the unit',
    type: 'money',
    category: 'Rental Price',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    },
    cast: 'USMALLINT'
  },
  [PriceColumns.EFFECTIVE_RENT]: {
    description: 'Effective rent for the unit',
    type: 'money',
    category: 'Rental Price',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    },
    cast: 'USMALLINT'
  },
  [PriceColumns.ASKING_RENT_PSF]: {
    description: 'Asking rent per square foot for the unit',
    type: 'money',
    category: 'Rental Price',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    },
    format: {
      format: FormatMoney.format.MONEY,
      decimals: 2
    },
    cast: 'FLOAT'
  },
  [PriceColumns.EFFECTIVE_RENT_PSF]: {
    description: 'Effective rent per square foot for the unit',
    type: 'money',
    category: 'Rental Price',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    },
    format: {
      format: FormatMoney.format.MONEY,
      decimals: 2
    },
    cast: 'FLOAT'
  },
  [PriceColumns.CONCESSION]: {
    description: 'Concession for the unit',
    type: 'money',
    category: 'Rental Price',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    },
    cast: 'USMALLINT'
  },
  [PriceColumns.AS_OF_WEEK]: {
    description:
      'Aggregate prices and leases by week. This is the first day of the week when the price or lease was effective',
    type: 'date',
    category: 'Time Breakdowns',
    nullable: true,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: false
    },
    exclusivityKey: 'time_breakdown'
  },
  [PriceColumns.AS_OF_MONTH]: {
    description:
      'Aggregate prices and leases by month. This is the first day of the month when the price or lease was effective',
    type: 'date',
    category: 'Time Breakdowns',
    nullable: true,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: false
    },
    exclusivityKey: 'time_breakdown'
  },
  [PriceColumns.AS_OF_QUARTER]: {
    description:
      'Aggregate prices and leases by quarter. This is the first day of the quarter when the price or lease was effective',
    type: 'date',
    category: 'Time Breakdowns',
    nullable: true,
    aggregations: [],
    usage: {
      column: true,
      mustAggregate: false,
      filter: false
    },
    exclusivityKey: 'time_breakdown'
  },
  // Lease Columns
  [PriceColumns.LEASE_START_DATE]: {
    description: 'Date when the lease period started',
    type: 'date',
    category: 'Lease History',
    nullable: true,
    aggregations: [StaticAggregate.MIN, StaticAggregate.MAX],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    }
  },
  [PriceColumns.LEASE_END_DATE]: {
    description: 'Date when the lease period ended',
    type: 'date',
    category: 'Lease History',
    nullable: true,
    aggregations: [StaticAggregate.MIN, StaticAggregate.MAX],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    }
  },
  [PriceColumns.LEASED_ASKING_RENT]: {
    description: 'Asking rent for the lease',
    type: 'money',
    category: 'Lease Data',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    },
    cast: 'USMALLINT'
  },
  [PriceColumns.LEASED_EFFECTIVE_RENT]: {
    description: 'Effective rent for the lease',
    type: 'money',
    category: 'Lease Data',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    },
    cast: 'USMALLINT'
  },
  [PriceColumns.LEASED_ASKING_RENT_PSF]: {
    description: 'Asking rent per square foot for the lease',
    type: 'money',
    category: 'Lease Data',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    },
    format: {
      format: FormatMoney.format.MONEY,
      decimals: 2
    },
    cast: 'FLOAT'
  },
  [PriceColumns.LEASED_EFFECTIVE_RENT_PSF]: {
    description: 'Effective rent per square foot for the lease',
    type: 'money',
    category: 'Lease Data',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    },
    format: {
      format: FormatMoney.format.MONEY,
      decimals: 2
    },
    cast: 'FLOAT'
  },
  [PriceColumns.LEASED_CONCESSION]: {
    description: 'Concession for the lease',
    type: 'money',
    category: 'Lease Data',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    },
    cast: 'USMALLINT'
  },
  [PriceColumns.LEASE_TERM]: {
    description: 'Duration of the lease in days',
    type: 'integer',
    category: 'Lease Data',
    nullable: true,
    aggregations: [
      StaticAggregate.AVG,
      StaticAggregate.MIN,
      StaticAggregate.MAX,
      StaticAggregate.SUM,
      ...generatePercentileAggregations([10, 25, 50, 75, 90])
    ],
    usage: {
      column: true,
      mustAggregate: true,
      filter: true
    },
    cast: 'USMALLINT'
  }
}

export interface ExampleReport {
  name: string
  exampleQuery?: string // Used to generate examples for LLM usage
  description: string
  query: DatasetPayload
  complexity: number // A value from 1 (simple) to 5 (complex)
}

export const exampleReports: ExampleReport[] = [
  {
    name: 'Rent Roll by Floorplan',
    exampleQuery:
      'Show asking rent, in-place rent, and estimated units by floorplan for all properties in my Portfolio report tag.',
    description:
      'Use rent roll snapshot data by floorplan without mixing unit-level history or occupancy columns.',
    query: {
      scopes: [
        { column: PropertyColumns.PROPERTY_ID },
        { column: PropertyColumns.BUILDING_NAME },
        { column: PropertyColumns.STREET_ADDRESS },
        { column: RentRollColumns.RENT_ROLL_AGGREGATION_FLOORPLAN },
        { column: RentRollColumns.RENT_ROLL_FLOORPLAN_NAME },
        { column: RentRollColumns.RENT_ROLL_BED },
        { column: RentRollColumns.RENT_ROLL_BATH },
        { column: RentRollColumns.RENT_ROLL_PARTIAL_BATH },
        {
          column: RentRollColumns.RENT_ROLL_ASKING_RENT,
          format: {
            format: FormatMoney.format.MONEY,
            decimals: 0
          }
        },
        {
          column: RentRollColumns.RENT_ROLL_IN_PLACE_RENT,
          format: {
            format: FormatMoney.format.MONEY,
            decimals: 0
          }
        },
        {
          column: RentRollColumns.RENT_ROLL_ESTIMATED_UNITS,
          format: {
            format: FormatNumber.format.NUMBER,
            decimals: 0,
            thousandsSeparator: true
          }
        },
        {
          column: RentRollColumns.RENT_ROLL_NUMBER_LISTINGS,
          format: {
            format: FormatNumber.format.NUMBER,
            decimals: 0,
            thousandsSeparator: true
          }
        },
        {
          column: RentRollColumns.RENT_ROLL_EXPOSURE_PERCENT,
          format: {
            format: FormatPercentage.format.PERCENTAGE,
            decimals: 1
          }
        },
        {
          column: RentRollColumns.RENT_ROLL_RENEWAL_PERCENT,
          format: {
            format: FormatPercentage.format.PERCENTAGE,
            decimals: 1
          }
        },
        {
          column: RentRollColumns.RENT_ROLL_DAYS_ON_MARKET,
          format: {
            format: FormatNumber.format.NUMBER,
            decimals: 0
          }
        },
        {
          column: RentRollColumns.RENT_ROLL_SQFT,
          format: {
            format: FormatNumber.format.NUMBER,
            decimals: 0,
            thousandsSeparator: true
          }
        }
      ] as ScopeColumn[],
      filters: {
        and: [
          {
            column: ReportColumns.REPORT_TAG,
            filter: { reportTagContains: ['Portfolio'], flag: false }
          }
        ]
      }
    },
    complexity: 2
  },
  {
    name: 'Property Data',
    exampleQuery: 'Show high-level property data for Chicago, IL',
    description: 'Gather property-level data for a given market',
    query: {
      scopes: [
        { column: MarketColumns.CITY },
        { column: MarketColumns.STATE },
        { column: MarketColumns.ZIP_CODE },
        { column: PropertyColumns.PROPERTY_ID },
        { column: PropertyColumns.BUILDING_NAME },
        { column: PropertyColumns.STREET_ADDRESS },
        { column: PropertyColumns.LAT },
        { column: PropertyColumns.LON },
        { column: PropertyColumns.NUMBER_STORIES },
        { column: PropertyColumns.NUMBER_UNITS },
        { column: PropertyColumns.MANAGEMENT_COMPANY },
        { column: PropertyColumns.AVG_REVIEW_SCORE },
        { column: PropertyColumns.YEAR_BUILT },
        { column: PropertyColumns.OVERALL_QUALITY_SCORE },
        { column: PropertyColumns.COUNT_REVIEWS },
        { column: PropertyColumns.IS_USING_REV_MANAGEMENT },
        { column: PropertyColumns.AVG_TIME_ON_MARKET_DAYS },
        { column: PropertyColumns.AVG_PRICE_CHANGE }
      ],
      filters: {
        and: [
          { column: MarketColumns.CITY, filter: { equals: 'Chicago' } },
          { column: MarketColumns.STATE, filter: { equals: 'IL' } }
        ]
      }
    },
    complexity: 1
  },
  {
    name: 'List Price Points For a Property',
    exampleQuery:
      'Average rents and square footage for all floorplans for OneEleven (zip code 60601)',
    description: 'What are all the recent price points recorded for a specific property?',
    query: {
      scopes: [
        { column: PropertyColumns.BUILDING_NAME },
        { column: PropertyColumns.STREET_ADDRESS },
        { column: UnitColumns.BED },
        { column: UnitColumns.BATH },
        { column: UnitColumns.FLOORPLAN_NAME },
        {
          column: UnitColumns.SQFT,
          format: {
            format: FormatNumber.format.NUMBER,
            decimals: 0,
            thousandsSeparator: true
          },
          aggregate: StaticAggregate.AVG
        },
        {
          column: PriceColumns.EFFECTIVE_RENT,
          format: {
            format: FormatMoney.format.MONEY,
            decimals: 0
          },
          aggregate: StaticAggregate.AVG
        },
        {
          column: PriceColumns.ASKING_RENT,
          format: {
            format: FormatMoney.format.MONEY,
            decimals: 0
          },
          aggregate: StaticAggregate.AVG
        }
      ] as ScopeColumn[],
      filters: {
        and: [
          { column: PropertyColumns.BUILDING_NAME, filter: { equals: 'OneEleven' } },
          { column: MarketColumns.ZIP_CODE, filter: { equals: '60601' } }
        ]
      }
    },
    complexity: 1
  },
  {
    name: 'Unix Mix',
    exampleQuery:
      'Average asking and effective rents, and count of units for all unit types of OneEleven (60601)?',
    description: 'Export the Unix Mix for a given property',
    query: {
      scopes: [
        { column: MarketColumns.ZIP_CODE },
        { column: PropertyColumns.PROPERTY_ID },
        { column: PropertyColumns.BUILDING_NAME },
        { column: PropertyColumns.STREET_ADDRESS },
        { column: UnitColumns.BED },
        { column: UnitColumns.BATH },
        { column: UnitColumns.FLOORPLAN_NAME },
        { column: UnitColumns.UNIT_ID, aggregate: StaticAggregate.COUNT_DISTINCT },
        {
          column: UnitColumns.SQFT,
          aggregate: StaticAggregate.AVG,
          format: {
            format: FormatNumber.format.NUMBER,
            decimals: 0
          }
        },
        {
          column: PriceColumns.ASKING_RENT,
          aggregate: StaticAggregate.AVG,
          format: {
            format: FormatMoney.format.MONEY,
            decimals: 0
          }
        },
        {
          column: PriceColumns.EFFECTIVE_RENT,
          aggregate: StaticAggregate.AVG,
          format: {
            format: FormatMoney.format.MONEY,
            decimals: 0
          }
        }
      ] as ScopeColumn[],
      filters: {
        and: [
          { column: PropertyColumns.BUILDING_NAME, filter: { equals: 'OneEleven' } },
          { column: MarketColumns.ZIP_CODE, filter: { equals: '60601' } }
        ]
      }
    },
    complexity: 1
  },
  {
    name: 'Portfolio Overview',
    exampleQuery: 'Show high-level property data for my reports with the Portfolio tag.',
    description: 'Gather property-level data for your portfolio based on a Report Tag',
    query: {
      scopes: [
        { column: PropertyColumns.BUILDING_NAME },
        { column: PropertyColumns.YEAR_BUILT },
        { column: PropertyColumns.NUMBER_UNITS },
        {
          column: LeasingActivityColumns.EXPOSURE_PERCENTAGE,
          aggregate: StaticAggregate.AVG,
          format: {
            format: FormatPercentage.format.PERCENTAGE,
            decimals: 1
          }
        },
        {
          column: LeasingActivityColumns.LEASED_PERCENTAGE,
          aggregate: StaticAggregate.AVG,
          format: {
            format: FormatPercentage.format.PERCENTAGE,
            decimals: 1
          }
        },
        {
          column: UnitColumns.SQFT,
          aggregate: StaticAggregate.AVG,
          format: {
            format: FormatNumber.format.NUMBER,
            decimals: 0,
            thousandsSeparator: true
          }
        },
        {
          column: PriceColumns.ASKING_RENT,
          aggregate: StaticAggregate.AVG,
          format: {
            format: FormatMoney.format.MONEY,
            decimals: 0
          }
        },
        {
          column: PriceColumns.CONCESSION,
          aggregate: StaticAggregate.AVG,
          format: {
            format: FormatMoney.format.MONEY,
            decimals: 0
          }
        },
        {
          column: PriceColumns.EFFECTIVE_RENT,
          aggregate: StaticAggregate.AVG,
          format: {
            format: FormatMoney.format.MONEY,
            decimals: 0
          }
        }
      ],
      filters: {
        and: [
          {
            column: ReportColumns.REPORT_TAG,
            filter: { reportTagContains: ['Portfolio'], flag: true, admin: true }
          },
          {
            column: PriceColumns.FROM_DATE,
            filter: { greaterThanOrEqualTo: format(subDays(new Date(), 30), 'yyyy-MM-dd') }
          }
        ]
      }
    },
    complexity: 2
  },
  {
    name: 'Average Rents by Property',
    exampleQuery: 'What are the average asking and effective rents for properties in 60657?',
    description:
      'What are the average asking and effective rents for each property within a specific zip code?',
    query: {
      scopes: [
        { column: MarketColumns.ZIP_CODE },
        { column: PropertyColumns.PROPERTY_ID },
        { column: PropertyColumns.BUILDING_NAME },
        { column: PropertyColumns.STREET_ADDRESS },
        {
          column: PriceColumns.ASKING_RENT,
          aggregate: StaticAggregate.AVG,
          format: {
            format: FormatMoney.format.MONEY,
            decimals: 0
          }
        },
        {
          column: PriceColumns.EFFECTIVE_RENT,
          aggregate: StaticAggregate.AVG,
          format: {
            format: FormatMoney.format.MONEY,
            decimals: 0
          }
        }
      ] as ScopeColumn[],
      filters: {
        and: [{ column: MarketColumns.ZIP_CODE, filter: { equals: '60657' } }]
      }
    },
    complexity: 2
  },
  {
    name: 'Average Rents by Zip Code',
    exampleQuery: 'What are the average asking and effective rents for all unit types in 60657?',
    description:
      'What are the average asking and effective rents across all unit types in a given zip code?',
    query: {
      scopes: [
        {
          column: MarketColumns.ZIP_CODE
        },
        {
          column: UnitColumns.BED
        },
        {
          column: UnitColumns.BATH
        },
        {
          column: PriceColumns.ASKING_RENT,
          alias: 'Asking Rent',
          aggregate: StaticAggregate.AVG,
          format: {
            format: FormatMoney.format.MONEY,
            decimals: 0
          }
        },
        {
          column: PriceColumns.EFFECTIVE_RENT,
          alias: 'Effective Rent',
          aggregate: StaticAggregate.AVG,
          format: {
            format: FormatMoney.format.MONEY,
            decimals: 0
          }
        }
      ] as ScopeColumn[],
      filters: {
        and: [{ column: MarketColumns.ZIP_CODE, filter: { equals: '60657' } }]
      }
    },
    complexity: 2
  },
  {
    name: 'Market Prices Over Time',
    exampleQuery: 'Show monthly rents, for all unit types, for all properties in Chicago, IL',
    description:
      'What are the monthly average asking and effective rents across all unit types in a given city?',
    query: {
      scopes: [
        { column: MarketColumns.STATE },
        { column: MarketColumns.CITY },
        { column: UnitColumns.BED },
        { column: UnitColumns.BATH },
        {
          column: UnitColumns.UNIT_ID,
          aggregate: StaticAggregate.COUNT_DISTINCT
        },
        {
          column: PriceColumns.AS_OF_MONTH,
          format: { format: FormatDate.format.DATE, dateFormat: 'MM/dd/yyyy' }
        },
        {
          column: PriceColumns.ASKING_RENT,
          aggregate: StaticAggregate.AVG,
          format: {
            format: FormatMoney.format.MONEY,
            decimals: 0
          }
        },
        {
          column: PriceColumns.EFFECTIVE_RENT,
          aggregate: StaticAggregate.AVG,
          format: {
            format: FormatMoney.format.MONEY,
            decimals: 0
          }
        }
      ] as ScopeColumn[],
      filters: {
        and: [
          { column: MarketColumns.CITY, filter: { equals: 'Chicago' } },
          { column: MarketColumns.STATE, filter: { equals: 'IL' } }
        ]
      }
    },
    complexity: 3
  }
]
